import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';

import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { invalidateTeamMemberWithRelatedQueries } from '@/modules/common/utils/query-invalidation';
import { resendInviteTeamMember } from '@/modules/team/api/team.api';
import { TeamMember } from '@/modules/team/types/team-member';

interface ResendInviteTeamMemberModalProps {
  teamMember: TeamMember;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * Modal for resending invitation to a team member with expired status.
 * Generates a new invitation link and updates expiry date.
 */
export function ResendInviteTeamMemberModal({
  teamMember,
  onSuccess,
  trigger,
  open,
  onOpen,
  onClose,
}: ResendInviteTeamMemberModalProps) {
  const { t } = useTranslation(['authenticated.team', 'common']);
  const queryClient = useQueryClient();
  const { displayToastErrorFromApiWithFallback } = useApiError();

  const resendInviteTeamMemberMutation = useMutation({
    mutationFn: (memberId: string) => resendInviteTeamMember(memberId),
    onSuccess: async () => {
      await invalidateTeamMemberWithRelatedQueries(queryClient, teamMember.id);
      onSuccess?.();
    },
    onError: error =>
      displayToastErrorFromApiWithFallback(error, t('authenticated.team:resendInvite.resendError')),
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Determine the actual modal open state (external or internal)
  const actualModalOpen = open !== undefined ? open : isModalOpen;

  const handleResendInvite = async (closeModal: () => void) => {
    try {
      await resendInviteTeamMemberMutation.mutateAsync(teamMember.id);
      closeModal();
    } catch (error) {
      // Error handled by mutation, keep modal open for retry
    }
  };

  const messageContent = (
    <div className="space-y-4">
      <div>
        <Trans
          i18nKey="authenticated.team:resendInvite.resendInviteTeamMemberMessage"
          values={{ name: teamMember.name, email: teamMember.email }}
          components={{ strong: <strong /> }}
        />
      </div>
      <div className="space-y-2">
        <p className="font-semibold">{t('authenticated.team:resendInvite.whatHappens')}</p>
        <ul className="ml-2 list-inside list-disc space-y-1">
          <li>{t('authenticated.team:resendInvite.newInviteLinkGenerated')}</li>
          <li>{t('authenticated.team:resendInvite.emailWillBeSent')}</li>
          <li>{t('authenticated.team:resendInvite.oldLinkWillExpire')}</li>
          <li>{t('authenticated.team:resendInvite.statusWillUpdate')}</li>
        </ul>
      </div>
    </div>
  );

  return (
    <ConfirmationModal
      trigger={trigger}
      title={t('authenticated.team:resendInvite.resendInviteTeamMember')}
      message={messageContent}
      confirmText={
        resendInviteTeamMemberMutation.isPending
          ? t('authenticated.team:resendInvite.resending')
          : t('authenticated.team:resendInvite.resendInviteTeamMember')
      }
      confirmVariant="default"
      onConfirm={handleResendInvite}
      isLoading={resendInviteTeamMemberMutation.isPending}
      open={actualModalOpen}
      onOpen={onOpen || (() => setIsModalOpen(true))}
      onClose={
        onClose ||
        (() => {
          setIsModalOpen(false);
        })
      }
    />
  );
}
